/**
 * Onboarding API
 * Domain API via HTTP transport (apiTransport) to the backend /api RPC.
 */

import { Lead } from '@repo/shared-types';
import { apiTransport as router } from './transport';
import { getContext } from './core';

const UPLOAD_SESSION_STORAGE_PREFIX = 'hb:onboardingUploadSession:';
const UPLOAD_SECRET_RPC = 'X-Upload-Session-Secret';

function persistUploadSessionSecret(token: string, secret: string): void {
  try {
    sessionStorage.setItem(UPLOAD_SESSION_STORAGE_PREFIX + token, secret);
  } catch {
    /* ignore */
  }
}

function loadUploadSessionSecret(token: string): string | null {
  try {
    return sessionStorage.getItem(UPLOAD_SESSION_STORAGE_PREFIX + token);
  } catch {
    return null;
  }
}

function requireUploadSessionSecret(token: string): string {
  const s = loadUploadSessionSecret(token);
  if (!s) throw new Error('Missing upload session. Re-upload your portfolio file.');
  return s;
}

/** Pre-signup portfolio upload (no auth). */
export const onboardingUploadApi = {
  /** POST /onboarding/upload with base64 content; returns upload token and one-time session secret. */
  upload: async (body: { content: string; fileName?: string; source?: 'json' | 'csv' }) => {
    const res = await router.post<{ upload_token: string; upload_session_secret: string }>('/onboarding/upload', body);
    if (res.upload_token && res.upload_session_secret) {
      persistUploadSessionSecret(res.upload_token, res.upload_session_secret);
    }
    return res;
  },
  /** GET /onboarding/upload/:token/status for polling. */
  status: async (token: string) => {
    const secret = requireUploadSessionSecret(token);
    return router.get<{ status: string; progress?: string }>(
      `/onboarding/upload/${encodeURIComponent(token)}/status`,
      undefined,
      undefined,
      { [UPLOAD_SECRET_RPC]: secret }
    );
  },
  /** GET /onboarding/upload/:token/result for review (parsed_payload + exceptions). */
  result: async (token: string) => {
    const secret = requireUploadSessionSecret(token);
    return router.get<{ parsed_payload?: unknown; exceptions?: unknown[] }>(
      `/onboarding/upload/${encodeURIComponent(token)}/result`,
      undefined,
      undefined,
      { [UPLOAD_SECRET_RPC]: secret }
    );
  },
  /** PUT /onboarding/upload/:token/result to persist review edits (parsed_payload, exceptions). */
  updateResult: async (token: string, body: { parsed_payload?: unknown; exceptions?: unknown[] }) => {
    const secret = requireUploadSessionSecret(token);
    return router.put<{ ok: true }>(
      `/onboarding/upload/${encodeURIComponent(token)}/result`,
      body,
      undefined,
      { [UPLOAD_SECRET_RPC]: secret }
    );
  },
};

export const onboardingApi = {
    leads: {
        list: async () => router.get<Lead[]>('/leasing/leads'),
        get: async (id: string) => router.get<Lead>(`/leasing/leads/${id}`),
        create: async (lead: Lead) => {
            const context = getContext();
            return router.post<Lead>('/leasing/leads', lead, context);
        },
        update: async (id: string, updates: Partial<Lead>) => {
            const context = getContext();
            return router.patch<Lead>(`/leasing/leads/${id}`, updates, context);
        },
        delete: async (id: string) => {
            const context = getContext();
            return router.delete<{ success: boolean }>(`/leasing/leads/${id}`, context);
        }
    },
    renewals: {
        list: async (filters?: { leaseId?: string }) => {
        const query = filters?.leaseId ? `?leaseId=${filters.leaseId}` : '';
        return router.get<import('@repo/shared-types/leasing/schemas').RenewalOffer[]>(`/leasing/renewals${query}`);
      },
    }
};
